import { Avatar, Col, Icon, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useEffect, useRef } from "react";
import { GUTTER } from "../chat/reading.ts";
import { say } from "../localization/say.ts";
import { calledBy } from "../me/called.ts";
import type { PersonInCall } from "./calling.ts";
import type { Speaker } from "./media.ts";
import { isLocalSpeaker } from "./room-controls.ts";

type MediaControl = {
  mic: boolean;
  camera: boolean;
  onMic: () => void;
  onCamera: () => void;
};

function Control({
  label,
  testID,
  onPress,
  danger,
  icon,
}: {
  label: string;
  testID: string;
  onPress: () => void;
  danger: boolean;
  icon: "mic" | "mic-off" | "video" | "video-off" | "phone-off";
}) {
  const theme = useTheme();
  return (
    <Tappable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={{
        width: 42,
        height: 42,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: theme.radius.round,
        backgroundColor: danger ? theme.color.danger : theme.color["bg.surface"],
      }}
    >
      <Icon name={icon} size={18} color={danger ? "text.onAccent" : "text.primary"} />
    </Tappable>
  );
}

function Tile({
  speaker,
  who,
  controls,
}: {
  speaker: Speaker;
  who: PersonInCall | undefined;
  controls?: MediaControl;
}) {
  const theme = useTheme();
  const holder = useRef<HTMLVideoElement | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const name = who ? (calledBy(who) ?? who.handle) : speaker.identity.slice(0, 8);

  useEffect(() => {
    const element = holder.current;
    if (!element) return;
    if (!speaker.video) {
      element.srcObject = null;
      return;
    }
    element.srcObject = new MediaStream([speaker.video]);
    void element.play().catch(() => undefined);
  }, [speaker.video]);

  useEffect(() => {
    const element = audio.current;
    if (!element) return;
    if (!speaker.audio) {
      element.srcObject = null;
      return;
    }
    element.srcObject = new MediaStream([speaker.audio]);
    void element.play().catch(() => undefined);
  }, [speaker.audio]);

  return (
    <Col
      testID="call-tile"
      style={{
        width: 220,
        aspectRatio: 16 / 10,
        borderRadius: theme.radius.md,
        borderWidth: speaker.speaking ? 2 : 1,
        borderColor: speaker.speaking ? theme.color.accent : theme.color["border.default"],
        backgroundColor: theme.color["bg.surfaceAlt"],
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        gap: theme.space[1],
      }}
    >
      {speaker.audio ? <audio ref={audio} autoPlay playsInline data-testid="call-audio" /> : null}
      {speaker.video ? (
        <video
          ref={holder}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Avatar name={name} size={44} />
      )}
      {controls ? (
        <Col
          testID="call-local-media-controls"
          style={{
            position: "absolute",
            top: theme.space[2],
            right: theme.space[2],
            flexDirection: "row",
            gap: theme.space[1],
          }}
        >
          <Control
            label={controls.mic ? say("call.mute") : say("call.unmute")}
            testID="call-mic"
            onPress={controls.onMic}
            danger={!controls.mic}
            icon={controls.mic ? "mic" : "mic-off"}
          />
          <Control
            label={controls.camera ? say("call.cameraOff") : say("call.cameraOn")}
            testID="call-camera"
            onPress={controls.onCamera}
            danger={!controls.camera}
            icon={controls.camera ? "video" : "video-off"}
          />
        </Col>
      ) : null}
      <Col
        style={{
          position: "absolute",
          left: theme.space[2],
          right: theme.space[2],
          bottom: theme.space[2],
          alignItems: "center",
        }}
      >
        <Text typeName="caption" color="text.primary" numberOfLines={1}>
          {speaker.muted ? say("call.mutedName")(name) : name}
        </Text>
      </Col>
    </Col>
  );
}

function Bar({ onLeave }: { onLeave: () => void }) {
  return <Control label={say("call.leave")} testID="call-leave" onPress={onLeave} danger icon="phone-off" />;
}

export function CallRoom({
  speakers,
  here,
  me,
  wired,
  broke,
  mic,
  camera,
  onMic,
  onCamera,
  onLeave,
}: {
  speakers: readonly Speaker[];
  here: readonly PersonInCall[];
  me: string;
  wired: boolean;
  broke: string | null;
  mic: boolean;
  camera: boolean;
  onMic: () => void;
  onCamera: () => void;
  onLeave: () => void;
}) {
  const theme = useTheme();
  const byAccount = new Map(here.map((one) => [one.account, one]));

  return (
    <Col
      testID="call-room"
      style={{
        paddingHorizontal: GUTTER,
        paddingBottom: theme.space[3],
        gap: theme.space[3],
        borderBottomWidth: 1,
        borderBottomColor: theme.color["border.default"],
      }}
    >
      <Col testID="call-stage" style={{ position: "relative", paddingBottom: 58 }}>
        <Col style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space[3] }}>
          {speakers.map((one) => (
            <Tile
              key={one.identity}
              speaker={one}
              who={byAccount.get(one.identity)}
              controls={isLocalSpeaker(one.identity, me) ? { mic, camera, onMic, onCamera } : undefined}
            />
          ))}
        </Col>
        <Col
          testID="call-controls-overlay"
          style={{ position: "absolute", left: 0, right: 0, bottom: 6, alignItems: "center" }}
        >
          <Bar onLeave={onLeave} />
        </Col>
      </Col>
      <Text typeName="caption" color="text.tertiary" testID="call-room-note">
        {broke ?? (wired ? say("call.phoneLocks") : say("call.dialling"))}
      </Text>
    </Col>
  );
}
